import { YES_NO, applicantSection, otherAttachments, proxyAttachments, replySection, representationSection } from "modules/ebiForms/common";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * E5. Pisma do straży gminnej w sprawach o wykroczenia: zawiadomienie o wykroczeniu albo oświadczenie właściciela
 * (posiadacza) pojazdu w odpowiedzi na wezwanie — art. 17 § 3 i art. 54 Kpw, art. 78 ust. 4 Prawa o ruchu drogowym.
 * Postępowanie wszczyna straż z urzędu; to nie jest wniosek interesanta. Brak wzoru ogólnopolskiego; oświadczenie jak
 * „Oświadczenie właściciela/posiadacza pojazdu” Straży Miejskiej w Katowicach (część A — wskazanie kierującego albo
 * osoby, której powierzono pojazd, część B — jej dane; https://www.strazmiejska.katowice.pl/pliki/oswiadczenieaib,142.pdf
 * — pobrany 2026-10-22), bez imion rodziców i miejsca urodzenia wskazanej osoby (minimalizacja). Zawiadomienie — treść
 * według zasad SM Warszawa i Gliwice z katalogu (miejsce, czas, opis, sprawca lub pojazd, zdjęcia). Pełnomocnictwo
 * w sprawach o wykroczenia jest zwolnione z opłaty skarbowej, więc bez załącznika o opłacie 17 zł.
 */
const NOTICE: EbiCondition = { field: "rodzajPisma", equals: "zawiadomienie" };
const STATEMENT: EbiCondition = { field: "rodzajPisma", equals: "oswiadczenie" };
const POINTED_PERSON: EbiCondition = { all: [STATEMENT, { field: "trescOswiadczenia", in: ["innyKierujacy", "powierzenie"] }] };
const SELF_DRIVER: EbiCondition = { all: [STATEMENT, { field: "trescOswiadczenia", equals: "kierowalem" }] };
const UNKNOWN_USE: EbiCondition = { all: [STATEMENT, { field: "trescOswiadczenia", equals: "bezWiedzy" }] };

const representation = representationSection();

const E5: IEbiFormDefinition = {
  code: "E5",
  version: 1,
  title: "Zawiadomienie o wykroczeniu lub oświadczenie właściciela pojazdu dla straży gminnej",
  category: EEbiFormCategory.Safety,
  office: EEbiFormOffice.MunicipalUnit,
  documentType: "Pismo",
  description: "Zawiadom straż gminną (miejską) o wykroczeniu albo odpowiedz na wezwanie do wskazania, komu powierzono pojazd.",
  keywords: ["straż miejska", "straż gminna", "wykroczenie", "zawiadomienie", "wezwanie", "właściciel pojazdu", "kierujący", "mandat"],
  organ: "Straż gminna (miejska)",
  legalBasis:
    "art. 17 § 3 i art. 54 ustawy z dnia 24 sierpnia 2001 r. — Kodeks postępowania w sprawach o wykroczenia; art. 78 ust. 4 ustawy z dnia 20 czerwca 1997 r. — Prawo o ruchu drogowym; ustawa z dnia 29 sierpnia 1997 r. o strażach gminnych",
  feeLabel: "bez opłaty",
  fee: "Bez opłaty; pełnomocnictwo w sprawach o wykroczenia jest zwolnione z opłaty skarbowej.",
  variantField: "rodzajPisma",
  variants: {
    zawiadomienie: { title: "Zawiadomienie o wykroczeniu", documentType: "Pismo" },
    oswiadczenie: { title: "Oświadczenie właściciela (posiadacza) pojazdu — odpowiedź na wezwanie", documentType: "Pismo" },
  },
  sections: [
    {
      code: "rodzaj",
      title: "Rodzaj pisma",
      fields: [
        {
          code: "rodzajPisma",
          type: "radio",
          label: "Składam",
          required: true,
          options: [
            { value: "zawiadomienie", label: "zawiadomienie o wykroczeniu" },
            { value: "oswiadczenie", label: "oświadczenie właściciela (posiadacza) pojazdu w odpowiedzi na wezwanie straży gminnej" },
          ],
        },
        {
          code: "infoPilne",
          type: "info",
          label:
            "W sytuacji zagrożenia życia, zdrowia lub mienia dzwoń pod numer alarmowy 112, a w sprawach pilnych do straży gminnej (miejskiej) pod numer 986. Zawiadomienie w Elektronicznym Biurze Interesanta nie jest anonimowe — straż otrzyma Twoje dane.",
          visibleWhen: NOTICE,
        },
        {
          code: "infoSad",
          type: "info",
          label:
            "Wniosek o uchylenie prawomocnego mandatu karnego (art. 101 Kpw — w terminie 7 dni od jego uprawomocnienia) oraz wniosek o ukaranie składany przez pokrzywdzonego jako oskarżyciela posiłkowego (art. 27 Kpw) składa się do sądu rejonowego, nie do straży gminnej.",
        },
      ],
    },
    applicantSection("Składający pismo"),
    // pełnomocnictwo w sprawach o wykroczenia jest zwolnione z opłaty skarbowej — bez pytania o zwolnienie rodzinne
    {
      ...representation,
      title: "W czyim imieniu składasz pismo",
      fields: representation.fields
        .filter((f) => f.code !== "pelnomocnictwoZwolnione")
        .map((f) => (f.code === "wImieniu" ? { ...f, label: "Składam pismo" } : f)),
    },
    {
      code: "zawiadomienie",
      title: "Zawiadomienie o wykroczeniu",
      visibleWhen: NOTICE,
      fields: [
        { code: "dataZdarzenia", type: "date", label: "Data zdarzenia", required: true, width: "half" },
        { code: "godzinaZdarzenia", type: "text", label: "Godzina (w przybliżeniu)", width: "half", maxLength: 20, placeholder: "np. 14:30" },
        {
          code: "miejsceZdarzenia",
          type: "text",
          label: "Miejsce zdarzenia (miejscowość, ulica, numer posesji lub opis miejsca)",
          required: true,
          maxLength: 300,
        },
        { code: "opisCzynu", type: "textarea", label: "Opis zdarzenia", required: true, maxLength: 5000 },
        {
          code: "sprawca",
          type: "textarea",
          label: "Sprawca lub pojazd (jeśli znane)",
          hint: "Np. marka, kolor i numer rejestracyjny pojazdu, opis wyglądu sprawcy.",
          maxLength: 1000,
        },
        { code: "swiadkowie", type: "textarea", label: "Świadkowie zdarzenia (jeśli są)", maxLength: 1000 },
        { code: "pokrzywdzony", type: "radio", label: "Czy jesteś osobą pokrzywdzoną tym wykroczeniem?", required: true, options: YES_NO },
      ],
    },
    {
      code: "wezwanie",
      title: "Wezwanie, na które odpowiadasz",
      visibleWhen: STATEMENT,
      fields: [
        { code: "nrSprawy", type: "text", label: "Numer sprawy lub interwencji z wezwania", required: true, width: "half", maxLength: 50 },
        { code: "nrRejestracyjny", type: "text", label: "Numer rejestracyjny pojazdu", required: true, width: "half", maxLength: 15 },
        { code: "marka", type: "text", label: "Marka pojazdu", width: "half", maxLength: 100 },
        { code: "dataZdarzeniaWezwanie", type: "date", label: "Data zdarzenia z wezwania", required: true, width: "half" },
        { code: "miejsceZdarzeniaWezwanie", type: "text", label: "Miejsce zdarzenia z wezwania", maxLength: 300 },
      ],
    },
    {
      code: "oswiadczenie",
      title: "Oświadczenie",
      visibleWhen: STATEMENT,
      fields: [
        {
          code: "trescOswiadczenia",
          type: "radio",
          label: "Pouczona/pouczony o treści art. 78 ust. 4 i 5 ustawy — Prawo o ruchu drogowym, jako właściciel (posiadacz) pojazdu oświadczam, że",
          required: true,
          options: [
            { value: "kierowalem", label: "w chwili zdarzenia sama/sam kierowałam/kierowałem pojazdem" },
            { value: "innyKierujacy", label: "wskazuję osobę, która kierowała pojazdem w chwili zdarzenia" },
            { value: "powierzenie", label: "wskazuję osobę, której powierzyłam/powierzyłem pojazd do kierowania lub używania w dniu zdarzenia" },
            {
              value: "bezWiedzy",
              label: "pojazd został użyty wbrew mojej woli i wiedzy przez nieznaną osobę, czemu nie mogłam/mogłem zapobiec",
            },
          ],
        },
        {
          code: "przyjecieMandatu",
          type: "radio",
          label: "Czy wyrażasz gotowość przyjęcia mandatu karnego?",
          hint: "Jeśli nie, straż gminna może skierować do sądu wniosek o ukaranie.",
          required: true,
          visibleWhen: SELF_DRIVER,
          options: YES_NO,
        },
        { code: "wskazanaImieNazwisko", type: "text", label: "Imię i nazwisko wskazanej osoby", required: true, visibleWhen: POINTED_PERSON, maxLength: 200 },
        { code: "wskazanaPesel", type: "pesel", label: "PESEL wskazanej osoby (jeśli go znasz)", visibleWhen: POINTED_PERSON, width: "half" },
        {
          code: "wskazanaPrawoJazdy",
          type: "text",
          label: "Numer prawa jazdy (jeśli go znasz)",
          visibleWhen: POINTED_PERSON,
          width: "half",
          maxLength: 30,
        },
        { code: "wskazanaAdres", type: "text", label: "Adres zamieszkania wskazanej osoby", required: true, visibleWhen: POINTED_PERSON, maxLength: 300 },
        {
          code: "wskazanaAdresDoreczen",
          type: "text",
          label: "Adres do doręczeń w kraju (jeśli inny niż adres zamieszkania)",
          visibleWhen: POINTED_PERSON,
          maxLength: 300,
        },
        {
          code: "wyjasnienia",
          type: "textarea",
          label: "Wyjaśnienia",
          hint: "Np. okoliczności użycia pojazdu, zgłoszenie kradzieży; dokumenty możesz dołączyć jako załączniki.",
          requiredWhen: UNKNOWN_USE,
          maxLength: 5000,
        },
      ],
    },
    replySection(),
  ],
  attachments: [
    {
      code: "dokumentacja",
      label: "Zdjęcia lub nagrania zdarzenia",
      hint: "Pliki w oryginalnej, wysokiej rozdzielczości (np. JPG, MP4, PDF) — z widoczną datą i miejscem, jeśli to możliwe.",
      visibleWhen: NOTICE,
      multiple: true,
    },
    {
      code: "dokumentyOswiadczenie",
      label: "Dokumenty potwierdzające wyjaśnienia",
      hint: "Np. umowa najmu lub użyczenia pojazdu, potwierdzenie zgłoszenia kradzieży.",
      visibleWhen: STATEMENT,
      multiple: true,
    },
    ...proxyAttachments().filter((a) => a.code !== "oplataPelnomocnictwo"),
    otherAttachments(),
  ],
};

export default E5;
